const modules = [
  {
    key: "fundamentals",
    title: "Digital marketing fundamentals",
    understand:
      "Understand the customer journey, positioning and the role of each channel.",
    practice: "Connect a real offer to a clear marketing objective.",
    produce: "A simple digital strategy you can put to work.",
  },
  {
    key: "content",
    title: "Content, social media and acquisition",
    understand:
      "Understand how content attracts attention, builds understanding and supports a decision.",
    practice: "Plan posts and adapt formats to the channels you choose.",
    produce: "A focused content plan built around a commercial objective.",
  },
  {
    key: "retention",
    title: "Acquisition, retention and automation",
    understand:
      "Understand the complementary roles of Meta Ads, Google Ads, SEO and follow-up.",
    practice: "Build a simple journey from acquisition through retention.",
    produce:
      "A prospect-to-customer flow with indicators and follow-up points.",
  },
  {
    key: "creative",
    title: "Graphic design and motion design",
    understand:
      "Understand the visual principles that make a message clear and consistent.",
    practice: "Create campaign-ready graphics and videos.",
    produce: "A set of content assets ready to test.",
  },
  {
    key: "commerce",
    title: "E-commerce, freelancing and opportunity development",
    understand:
      "Understand how to turn a product or skill into an offer you can test.",
    practice:
      "Explore sourcing, online selling and the structure of a freelance offer.",
    produce: "A documented experiment with clear decision criteria.",
  },
  {
    key: "ai",
    title: "AI applied to marketing",
    understand:
      "Understand where AI can accelerate research, creation, analysis and automation.",
    practice:
      "Write effective instructions and check the quality of AI-assisted output.",
    produce:
      "An AI-assisted workflow with a clear objective and human oversight.",
  },
];

const method = {
  title: "Learn by doing, then improve",
  introduction:
    "Every concept becomes an action, feedback and a stronger next version.",
  steps: ["Learn", "Apply", "Get feedback", "Test", "Measure", "Improve"],
  delivery:
    "Whether you attend in Douala or online, you follow the same practical progression and discuss your exercises.",
};

const laboratory = {
  title: "The e-commerce laboratory",
  introduction:
    "Work through the full path from an initial idea to a decision grounded in observation.",
  stages: [
    "Product",
    "Offer",
    "Content",
    "Acquisition",
    "Sale",
    "Analysis",
    "Optimisation",
  ],
  note: "The goal is to learn how to test, measure and decide. A product’s profitability depends on the market and execution.",
};

const bonuses = {
  title: "Tools and pathways to keep practising",
  items: [
    {
      title: "One month of premium tools",
      description:
        "Planned access to Canva Pro, CapCut Pro, Claude AI Max with Higgsfield, and HeyGen AI Pro.",
    },
    {
      title: "Opportunity guidance",
      description:
        "Practical direction for employment, freelancing and building an online activity.",
    },
    {
      title: "China sourcing",
      description:
        "Support in understanding the stages and decisions involved in a sourcing process.",
    },
    {
      title: "Shopikat and KURSA",
      description:
        "Guidance on structuring an online-selling experiment with the stated solutions.",
    },
  ],
};

const proof = {
  title: "Real proof, once it has been approved",
  introduction:
    "This area will contain only authentic media and feedback approved for publication by CSL BRANDS.",
  placeholders: [
    "[TESTIMONIAL TO BE SUPPLIED — profile, verified feedback and authorised photo]",
    "[REAL PHOTO TO BE SUPPLIED — training session at the centre]",
  ],
  testimonials: [],
};

const investment = {
  title: "A clear investment before you decide",
  introduction:
    "Tuition and registration are shown separately, then added from the program’s official data.",
  trainingFeeLabel: "Tuition",
  registrationFeeLabel: "Registration",
  totalLabel: "Total to plan for",
  logisticsLabel: "Duration, intake and delivery mode",
  included: [
    "All six modules",
    "Guided practice",
    "The e-commerce laboratory",
    "Confirmed bonuses",
  ],
  helper:
    "Choose your delivery mode. The next step opens WhatsApp with your program details already included.",
};

const baseFaq = [
  {
    key: "beginner",
    question: "Can I join as a complete beginner?",
    answer:
      "Yes. The program starts with the fundamentals and moves into practical exercises. Your commitment to practising remains essential.",
  },
  {
    key: "computer",
    question: "Do I need my own computer?",
    answer:
      "A personal computer is optional, and workstations may be available at the centre. A smartphone is required.",
  },
  {
    key: "delivery",
    question: "What is the difference between in-person and online delivery?",
    answer:
      "In-person sessions take place in Douala. Online delivery lets you follow the same learning progression remotely.",
  },
  {
    key: "booking",
    question: "How do I reserve my place?",
    answer:
      "Choose In person or Online, then send the pre-filled WhatsApp message. The team will share the registration steps.",
  },
  {
    key: "profitability",
    question: "Will the training make my activity profitable?",
    answer:
      "No specific commercial outcome is promised. You will learn to test an offer, read indicators and adjust your decisions; results depend on the market and your execution.",
  },
  {
    key: "after",
    question: "What happens after the program?",
    answer:
      "You leave with your exercises, working methods and pathways to continue towards employment, freelancing or an online project.",
  },
  {
    key: "documents",
    question: "Which documents do I need?",
    answer:
      "A national identity card or passport is required. The team will confirm the full list during registration.",
  },
  {
    key: "language",
    question: "Can I study in French or English?",
    answer:
      "Yes. You must be able to read and write French or English. Confirm your preferred language with the team when registering.",
  },
  {
    key: "requirements",
    question: "What are the participation requirements?",
    answer:
      "The program is intended for learners aged 22 or older. You need a smartphone and valid identification.",
  },
];

const buildContent = ({
  seo,
  hero,
  audience,
  transformation,
  capabilities,
  proofContent = proof,
  specificFaq,
  finalCta,
  whatsappTemplate,
  media,
}) => ({
  seo,
  hero,
  audience,
  transformation,
  capabilities,
  modules,
  method,
  laboratory,
  bonuses,
  proof: proofContent,
  media,
  investment,
  faq: [...baseFaq, specificFaq],
  finalCta,
  whatsappTemplate,
});

export const catalogue = {
  seo: {
    title: "Digital Marketing, E-commerce and AI Training | CSL BRANDS",
    description:
      "Compare five practical programs from one month to one year, in Douala or online, and choose the depth that fits your project.",
    keywords: [
      "digital marketing training Douala",
      "e-commerce training Cameroon",
      "AI marketing training",
    ],
    image: "/training/749514240_1018844154195700_8314658569172507857_n.jpg",
  },
  eyebrow: "Digital Marketing · E-commerce · AI",
  title: "Choose the program that fits your next step",
  introduction:
    "Five timeframes, one connected approach: learn, practise, measure and improve through a real or simulated project.",
  offersLabel: "Five training programs",
  detailCta: "View the program",
  compareLabels: {
    bestFor: "Best for",
    approach: "Approach",
  },
  comparator: {
    eyebrow: "Program finder",
    title: "Find the program that fits you",
    introduction:
      "Three questions, one at a time, to identify the format closest to your goal, available time and experience.",
    disclaimer:
      "The match is based only on your answers. It does not rank the programs and does not replace a conversation with the team.",
    stepCounter: "Step {current} of {total}",
    goalLabel: "What is your goal?",
    timeLabel: "How much time can you invest?",
    experienceLabel: "Where are you starting from?",
    reasonGoal: "Goal",
    reasonTime: "Time",
    reasonExperience: "Experience",
    next: "Continue",
    seeResult: "See my result",
    back: "Back",
    edit: "Edit my answers",
    goals: [
      {
        value: "foundations",
        label: "Build the foundations",
        description: "Clarify the basics and complete a first guided practice.",
      },
      {
        value: "practice",
        label: "Practise and improve",
        description: "Repeat, get feedback and correct your execution.",
      },
      {
        value: "project",
        label: "Launch an e-commerce project",
        description: "Connect acquisition, content, sales and measurement around one project.",
      },
      {
        value: "career",
        label: "Build a portfolio",
        description: "Produce demonstrable work and prepare a professional pathway.",
      },
      {
        value: "expertise",
        label: "Go deeper over time",
        description: "Develop more ambitious projects and grow your autonomy.",
      },
    ],
    times: [
      { value: "1", label: "1 month" },
      { value: "2-3", label: "2 to 3 months" },
      { value: "6", label: "6 months" },
      { value: "12", label: "1 year" },
    ],
    experiences: [
      { value: "beginner", label: "I am starting out" },
      { value: "practicing", label: "I already practise" },
      { value: "advanced", label: "I want to professionalise" },
    ],
    pendingEyebrow: "Your result",
    pendingTitle: "Your program will appear here",
    pendingBody:
      "Answer the three questions, one at a time, to see the program closest to your needs.",
    resultEyebrow: "Your match",
    whyTitle: "Why this program?",
    cta: "View this program",
    compareCta: "View all five programs",
    closestNote:
      "With the time available, this is the closest program. A longer format would be needed to go further.",
    reset: "Start again",
    matchBadge: "Your match",
  },
  methodTitle: "One shared method, with more depth at every stage",
  methodSummary:
    "Every program connects strategy, content, acquisition, creative work, e-commerce and AI. The duration determines how many cycles of practice, feedback and improvement you complete.",
  offerCards: [
    {
      key: "essential",
      name: "Essential",
      positioning:
        "Build clear foundations and complete a first guided implementation.",
      bestFor: "Beginner or first taster",
      approach: "Foundations + first practice",
    },
    {
      key: "progression",
      name: "Progression",
      positioning:
        "Repeat, get feedback and improve your execution over two months.",
      bestFor: "Learner who wants to correct their execution",
      approach: "Repeated practice + feedback",
    },
    {
      key: "immersion",
      name: "Immersion",
      positioning:
        "Connect the full marketing and e-commerce system around one project.",
      bestFor: "E-commerce project owner",
      approach: "End-to-end project, from idea to measurement",
    },
    {
      key: "professional",
      name: "Professional",
      positioning:
        "Produce demonstrable work and prepare a professional pathway.",
      bestFor: "Career change or portfolio",
      approach: "Portfolio + professional pathway",
    },
    {
      key: "expert",
      name: "Expert",
      positioning: "Deepen your skills and projects over a one-year cycle.",
      bestFor: "Practitioner who wants to go deeper",
      approach: "Long cycle + growing autonomy",
    },
  ],
};

export const offers = {
  essential: buildContent({
    seo: {
      title: "Essential 1-Month Program | Digital Marketing and E-commerce",
      description:
        "Move from uncertainty to a simple digital method in one month through a first guided implementation, in Douala or online.",
      keywords: [
        "digital marketing training for beginners",
        "CSL BRANDS Essential program",
        "one-month digital training",
      ],
      image:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-5.jpeg",
    },
    hero: {
      eyebrow: "Essential program · 1 month",
      headline: "In one month, stop leaving your digital efforts to chance.",
      support:
        "Learn the foundations you need to create, publish, sell and measure with a clear method.",
      primaryCta: "Reserve the Essential program",
      secondaryCta: "See what I will learn",
    },
    audience: [
      "I am starting from scratch and need a clear method.",
      "I already sell, but I do not know what to publish or how to measure it.",
      "I want a first skill I can apply right away.",
      "I want to explore the field before choosing a longer program.",
    ],
    transformation: {
      title: "From uncertainty to a practical starting method",
      before:
        "Scattered tools, posts without a purpose and a sales process that is hard to explain.",
      after:
        "A simple offer, a focused content plan, an acquisition approach and a first measurable experiment.",
    },
    capabilities: [
      "Structure a simple offer and define its marketing objective.",
      "Create a short sales-focused content plan.",
      "Understand how Meta Ads, SEO and Google Ads support acquisition.",
      "Set up a first prospect-to-customer journey.",
      "Test an e-commerce idea through product, offer and measurement logic.",
      "Use AI to accelerate research and creation without losing strategic direction.",
    ],
    specificFaq: {
      key: "one-month-depth",
      question: "Is one month enough to learn something useful?",
      answer:
        "The program focuses on solid foundations and a first implementation. It gives you a working starting system, not the depth of a longer cycle.",
    },
    finalCta: {
      headline: "Start with a simple system you can actually use.",
      support:
        "One month to clarify your foundations and complete a first guided experiment.",
      summary: "Essential · 1 month · Douala or online",
      cta: "Reserve the Essential program",
    },
    whatsappTemplate:
      "Hello CSL BRANDS, I would like to reserve a place in the Essential 1-month program starting on 15 September 2026. I would like to attend {MODE}. Please send me the registration details.",
    media: {
      transformation:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-8.jpeg",
      method:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-9.jpeg",
      laboratory:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-10.jpg",
      proof: [
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-11.jpg",
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-12.jpg",
      ],
    },
  }),
  progression: buildContent({
    seo: {
      title: "Progression 2-Month Program | Digital Marketing and E-commerce",
      description:
        "Turn digital foundations into repeatable skills through two months of practice, feedback and improvement.",
      keywords: [
        "practical digital marketing training",
        "CSL BRANDS Progression program",
        "two-month digital training",
      ],
      image:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-6.jpeg",
    },
    hero: {
      eyebrow: "Progression program · 2 months",
      headline: "Two months to move from “I know” to “I can do it.”",
      support:
        "Work through all six areas, practise more and learn how to improve work that is not producing the intended result.",
      primaryCta: "Reserve the Progression program",
      secondaryCta: "See what I will learn",
    },
    audience: [
      "I have watched tutorials, but I still lack structured practice.",
      "I already publish content without knowing what works.",
      "I want to connect content, acquisition and sales.",
      "I want enough guidance to try, get feedback and improve.",
    ],
    transformation: {
      title: "From isolated knowledge to repeatable execution",
      before:
        "Accumulated knowledge, limited practice and no clear loop for improving weak work.",
      after:
        "A connected strategy, repeated production cycles, readable indicators and feedback-led improvements.",
    },
    capabilities: [
      "Build a coherent digital strategy around an offer.",
      "Plan and produce content for an acquisition objective.",
      "Practise the main paid and organic acquisition channels.",
      "Create a simple acquisition, follow-up and retention flow.",
      "Produce graphics and videos that support conversion.",
      "Run several cycles of testing, measurement and improvement.",
      "Structure a first freelance offer or e-commerce experiment.",
    ],
    specificFaq: {
      key: "extra-month",
      question: "What does the second month add?",
      answer:
        "It adds repetition, feedback and time to improve your work. The focus is stronger execution, not simply more theory.",
    },
    finalCta: {
      headline: "Turn understanding into a practice you can repeat.",
      support:
        "Two months to produce, measure, get feedback and improve your execution.",
      summary: "Progression · 2 months · Douala or online",
      cta: "Reserve the Progression program",
    },
    whatsappTemplate:
      "Hello CSL BRANDS, I would like to reserve a place in the Progression 2-month program starting on 15 September 2026. I would like to attend {MODE}. Please send me the registration details.",
    media: {
      transformation:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-9.jpeg",
      method:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-10.jpg",
      laboratory:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-11.jpg",
      proof: [
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-12.jpg",
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-13.jpg",
      ],
    },
  }),
  immersion: buildContent({
    seo: {
      title: "Immersion 3-Month Program | Digital Marketing and E-commerce",
      description:
        "Build, test and improve a connected marketing and e-commerce system around a practical project.",
      keywords: [
        "practical e-commerce training",
        "CSL BRANDS Immersion program",
        "three-month digital marketing training",
      ],
      image:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce.jpeg",
    },
    hero: {
      eyebrow: "Immersion program · 3 months",
      headline:
        "Three months to build, test and improve a real digital sales system.",
      support:
        "Go beyond isolated tools. Connect strategy, content, acquisition, e-commerce, analysis and AI in one practical experience.",
      primaryCta: "Reserve the Immersion program",
      secondaryCta: "See what I will learn",
    },
    audience: [
      "I want a real immersion, not a sequence of disconnected classes.",
      "I want to launch or test an e-commerce activity.",
      "I want to understand how the different levers work together.",
      "I want enough time to measure a project and then improve it.",
    ],
    transformation: {
      title: "From an idea to one connected end-to-end system",
      before:
        "An isolated product or project, disconnected marketing actions and limited evidence for decision-making.",
      after:
        "A testable offer, a content and acquisition system, an online sales journey and a documented experiment.",
    },
    capabilities: [
      "Turn an idea into a structured, testable offer.",
      "Create a connected content and acquisition system.",
      "Practise China or Nigeria sourcing in a learning environment.",
      "Build an online sales journey with performance measurement.",
      "Use data to decide what to stop, improve or expand.",
      "Automate selected marketing tasks with a clear method.",
      "Document an end-to-end e-commerce experiment.",
    ],
    specificFaq: {
      key: "laboratory-project",
      question: "Do I need an existing product or project?",
      answer:
        "No. You can work on a real or simulated case. The laboratory helps you structure the stages and decision criteria for an experiment.",
    },
    finalCta: {
      headline: "Build the complete system, then learn how to improve it.",
      support:
        "Three months to connect product, offer, content, acquisition, sales and analysis around one project.",
      summary: "Immersion · 3 months · Douala or online",
      cta: "Reserve the Immersion program",
    },
    whatsappTemplate:
      "Hello CSL BRANDS, I would like to reserve a place in the Immersion 3-month program starting on 15 September 2026. I would like to attend {MODE}. Please send me the registration details.",
    media: {
      transformation:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-10.jpg",
      method:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-11.jpg",
      laboratory:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-12.jpg",
      proof: [
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-13.jpg",
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-14.jpg",
      ],
    },
  }),
  professional: buildContent({
    seo: {
      title: "Professional 6-Month Program | Digital Marketing and E-commerce",
      description:
        "Deepen your skills, produce demonstrable work and prepare a pathway into employment, freelancing or entrepreneurship.",
      keywords: [
        "professional digital marketing training",
        "CSL BRANDS Professional program",
        "digital marketing portfolio",
      ],
      image:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-2.jpeg",
    },
    hero: {
      eyebrow: "Professional program · 6 months",
      headline:
        "Six months to build skills you can demonstrate, not just claim.",
      support:
        "Go deeper into all six modules, produce work you can show and prepare your next step into employment, freelancing or entrepreneurship.",
      primaryCta: "Reserve the Professional program",
      secondaryCta: "See what I will learn",
    },
    audience: [
      "I am planning a career change and want evidence of my skills.",
      "I lack projects I can show to an employer or client.",
      "I want enough support to make substantial progress.",
      "I want a structured pathway towards employment, freelancing or entrepreneurship.",
    ],
    transformation: {
      title: "From developing skills to demonstrable work",
      before:
        "Skills that are hard to present, few documented projects and an unclear professional direction.",
      after:
        "Several portfolio-ready deliverables, decisions supported by indicators and a structured approach to opportunities.",
    },
    capabilities: [
      "Progressively master strategy, content, acquisition, design, e-commerce and AI.",
      "Build several deliverables that can support a portfolio.",
      "Present a strategy and justify decisions through objectives and indicators.",
      "Structure a freelance offer or an employment-search approach.",
      "Run marketing and e-commerce experiments with analysis and optimisation.",
      "Use AI for production, research, analysis and automation.",
      "Prepare the transition towards professional opportunities.",
    ],
    proofContent: {
      ...proof,
      certification: {
        labels: ["DQP", "AQP"],
        confirmationNote:
          "Details of the issuing body, equivalence and recognition must be confirmed with the CSL BRANDS team.",
      },
    },
    specificFaq: {
      key: "portfolio",
      question: "What kind of work could go into my portfolio?",
      answer:
        "You will produce several marketing and e-commerce deliverables. Their final form depends on the exercises and projects completed during the program.",
    },
    finalCta: {
      headline: "Build skills your work can demonstrate.",
      support:
        "Six months to go deeper, produce, document and prepare your next professional step.",
      summary: "Professional · 6 months · Douala or online",
      cta: "Reserve the Professional program",
    },
    whatsappTemplate:
      "Hello CSL BRANDS, I would like to reserve a place in the Professional 6-month program starting on 15 September 2026. I would like to attend {MODE}. Please send me the registration details.",
    media: {
      transformation:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-11.jpg",
      method:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-12.jpg",
      laboratory:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-13.jpg",
      proof: [
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-14.jpg",
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-15.jpg",
      ],
    },
  }),
  expert: buildContent({
    seo: {
      title: "Expert 1-Year Program | Digital Marketing, E-commerce and AI",
      description:
        "Build a deeper professional pathway over one year through projects, repeated practice and increasing autonomy.",
      keywords: [
        "one-year digital marketing training",
        "CSL BRANDS Expert program",
        "e-commerce and AI training",
      ],
      image:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-4.jpeg",
    },
    hero: {
      eyebrow: "Expert program · 1 year",
      headline:
        "One year to turn learning into a real professional pathway in digital.",
      support:
        "Go deeper into marketing, acquisition, creative work, e-commerce and AI, with more time to practise, build, document and consolidate your skills.",
      primaryCta: "Apply for the Expert program",
      secondaryCta: "See what I will learn",
    },
    audience: [
      "I am serious about a career change and need to understand what a year adds.",
      "I want more than a short course or a list of tools.",
      "I want to build evidence of my skills over time.",
      "I want a clear path connecting learning, projects and opportunities.",
    ],
    transformation: {
      title: "From learning to progressive autonomy",
      before:
        "Fragmented knowledge, few project cycles and limited evidence of long-term progression.",
      after:
        "A progressive portfolio, more advanced workflows and a stronger ability to analyse, present and improve a strategy.",
    },
    capabilities: [
      "Deepen all six modules through more cycles of practice and feedback.",
      "Build a progressive portfolio around marketing and e-commerce projects.",
      "Analyse, present and improve a strategy.",
      "Structure e-commerce experiments with greater autonomy.",
      "Create content, acquisition, retention and automation processes.",
      "Use AI in more advanced marketing workflows.",
      "Prepare an employment, freelance or business pathway supported by evidence of your work.",
    ],
    proofContent: {
      ...proof,
      certification: {
        labels: ["DQP", "AQP", "International certification"],
        confirmationNote:
          "Details of the issuing body, equivalence and recognition must be confirmed with the CSL BRANDS team.",
      },
    },
    specificFaq: {
      key: "one-year-depth",
      question: "What difference does a one-year program make?",
      answer:
        "The longer cycle allows more project rounds, feedback, documentation and progressively independent work than a short program.",
    },
    finalCta: {
      headline: "Give your progress enough time to become a pathway.",
      support:
        "One year to deepen all six modules, build projects and consolidate your autonomy.",
      summary: "Expert · 1 year · Douala or online",
      cta: "Apply for the Expert program",
    },
    whatsappTemplate:
      "Hello CSL BRANDS, I would like information about the Expert 1-year program starting on 15 September 2026. I would like to attend {MODE}. Please send me the application process and payment options.",
    media: {
      transformation:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-12.jpg",
      method:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-13.jpg",
      laboratory:
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-14.jpg",
      proof: [
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-15.jpg",
        "/training/csl-brands-formation-en-marketing-digital-e-commerce-8.jpeg",
      ],
    },
  }),
};
